import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, BarChart3, ExternalLink, Loader2, Trash2 } from 'lucide-react';
import { Modal } from './Modal';
import {
  useReportLink,
  useSetReportLink,
  useDeleteReportLink,
} from '@/hooks/useReport';

interface ReportLinkButtonProps {
  executionId: string;
  /** Only fetch the link when the card is actually visible (lazy). */
  enabled?: boolean;
  /** "Report" instead of "Report Dashboard", for cards short on width. */
  shortLabel?: boolean;
}

/**
 * Card action for the monitoring report:
 *  - no report linked → a "+" that opens a modal to paste the report id
 *  - report linked     → a "Report Dashboard" button that opens the dashboard
 */
export function ReportLinkButton({ executionId, enabled = true, shortLabel = false }: ReportLinkButtonProps) {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reportIdInput, setReportIdInput] = useState('');

  const { data, isLoading } = useReportLink(executionId, enabled);
  const setLink = useSetReportLink(executionId);
  const deleteLink = useDeleteReportLink(executionId);

  const link = data?.link ?? null;

  const handleSave = () => {
    const value = reportIdInput.trim();
    if (!value) return;
    setLink.mutate(value, {
      onSuccess: () => {
        setIsModalOpen(false);
        setReportIdInput('');
      },
    });
  };

  if (isLoading) {
    return (
      <span className="flex items-center gap-1 px-2 sm:px-3 py-2 sm:py-1.5 text-adv-tertiary">
        <Loader2 size={14} className="animate-spin" />
      </span>
    );
  }

  if (link) {
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={() => navigate(`/campaigns/${executionId}/report`)}
          className="adv-btn adv-btn--secondary adv-btn--sm min-h-[44px] sm:min-h-0 whitespace-nowrap"
          title={`Open report ${link.reportId}`}
        >
          <BarChart3 size={14} className="flex-shrink-0" />
          {shortLabel ? 'Report' : 'Report Dashboard'}
          <ExternalLink size={12} className="opacity-60 flex-shrink-0" />
        </button>
        <button
          onClick={() => deleteLink.mutate()}
          disabled={deleteLink.isPending}
          className="adv-btn adv-btn--ghost adv-btn--icon adv-btn--sm hover:text-adv-warning-text min-h-[44px] sm:min-h-0"
          title="Unlink report"
        >
          {deleteLink.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Trash2 size={14} />
          )}
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className="adv-btn adv-btn--secondary adv-btn--sm min-h-[44px] sm:min-h-0 whitespace-nowrap"
        title="Link a monitoring report"
      >
        <Plus size={14} className="flex-shrink-0" />
        Report
      </button>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Link monitoring report"
        maxWidth="md"
      >
        <div className="space-y-4">
          <p className="text-adv-body-m text-adv-secondary">
            Paste the report id. The dashboard is built from{' '}
            <code className="adv-num px-1 py-0.5 bg-adv-sunken rounded-adv-sm text-adv-code-xs">
              {'{report-id}'}/job-result.json
            </code>{' '}
            in the report bucket.
          </p>
          <input
            type="text"
            value={reportIdInput}
            onChange={(e) => setReportIdInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
            }}
            placeholder="report id"
            autoFocus
            className="adv-input adv-num w-full"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setIsModalOpen(false)}
              className="adv-btn adv-btn--secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!reportIdInput.trim() || setLink.isPending}
              className="adv-btn adv-btn--primary"
            >
              {setLink.isPending && <Loader2 size={14} className="animate-spin" />}
              Link report
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
